import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useAuth } from '@/contexts/AuthContext'
import { useEdition } from '@/hooks/useEdition'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireResearcher?: boolean
  /** Restrict to admin accounts (can_view_admin_panel). */
  requireAdmin?: boolean
  /** Part of the research workshop — not in the single-measurement edition. */
  requirePro?: boolean
}

export default function ProtectedRoute({
  children,
  requireResearcher = false,
  requireAdmin = false,
  requirePro = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, currentView, user } = useAuth()
  const { isBasic } = useEdition()
  const location = useLocation()

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Every account is both researcher and respondent; researcher-only routes
  // are gated by the currently selected view (Airbnb-style toggle), so a user
  // browsing as a respondent is sent to the respondent dashboard instead.
  if (requireResearcher && currentView !== 'researcher') {
    return <Navigate to="/participant/dashboard" replace />
  }

  // Admin analytics panel: only accounts flagged in Django admin get in;
  // everyone else is bounced to their normal dashboard rather than shown a
  // page they can't populate.
  if (requireAdmin && !user?.can_view_admin_panel) {
    return <Navigate to="/dashboard" replace />
  }

  // The single-measurement edition simply does not have these screens. Sending
  // the account to its dashboard beats a "you don't have this" page for
  // something it was never sold — and this is the only per-route hook there is,
  // because the researcher shell is mounted by each page, not by the router.
  if (requirePro && isBasic) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}


























